/**
 * EventsPage — create and run a meeting, whether or not you are flying to it.
 *
 * § G11. Until this existed, a group could only be created from INSIDE a trip
 * you owned, which quietly required the organizer to be a traveller. The people
 * who most often organise group travel are the ones who are not: an assistant
 * booking for a team they are not on, or a host who lives in the destination
 * city and will never have a flight to it.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CalendarDays, MapPin, Plus, Users } from 'lucide-react';
import { travelGroupApi, type TravelGroupSummary } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { anchorHeadline } from '@/components/trips/organizerRosterView';
import { localInputToIso, browserTimezone, eventSubtitle } from './eventsPageView';
// § G24 — the wall clock goes to the server as a wall clock. Only the server
// knows the destination city's timezone, and turning "11:00" into an instant is
// exactly the step that must happen in the zone the meeting is IN.

function CreateEventForm({ onCreated }: { onCreated: (id: string) => void }) {
  const [name, setName] = useState('');
  const [destination, setDestination] = useState('');
  const [startsAt, setStartsAt] = useState('');
  const [error, setError] = useState('');

  const mut = useMutation({
    mutationFn: () =>
      travelGroupApi.create({
        name: name.trim(),
        destination_airport: destination.trim().toUpperCase(),
        // The organizer types a wall-clock time meaning the MEETING CITY's clock
        // (§ G24). `anchor_start_local` is that string, converted server-side
        // through the destination airport's zone; the ISO instant and browser zone
        // ride along only as the fallback for an airport whose zone we do not know.
        anchor_start_local: startsAt || null,
        anchor_start_at: localInputToIso(startsAt),
        anchor_timezone: browserTimezone(),
      }),
    onSuccess: (g) => {
      setName('');
      setDestination('');
      setStartsAt('');
      setError('');
      onCreated(g.id);
    },
    onError: (e: any) => setError(e?.response?.data?.error || e.message),
  });

  return (
    <form
      className="flex flex-wrap items-end gap-2 rounded-lg border border-border bg-background p-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (name.trim() && destination.trim()) mut.mutate();
      }}
    >
      <label className="flex flex-col gap-1">
        <span className="text-xs text-muted-foreground">Meeting</span>
        <Input
          placeholder="Q3 planning meeting"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="h-8 w-56 text-sm"
        />
      </label>
      <label className="flex flex-col gap-1">
        <span className="text-xs text-muted-foreground">City airport</span>
        <Input
          placeholder="ATL"
          value={destination}
          onChange={(e) => setDestination(e.target.value.toUpperCase())}
          className="h-8 w-24 font-mono text-sm"
        />
      </label>
      <label className="flex flex-col gap-1">
        <span className="text-xs text-muted-foreground">Starts</span>
        <Input
          type="datetime-local"
          value={startsAt}
          onChange={(e) => setStartsAt(e.target.value)}
          className="h-8 w-52 text-sm"
        />
      </label>
      <Button type="submit" size="sm" className="h-8" disabled={mut.isPending || !name.trim() || !destination.trim()}>
        {mut.isPending ? 'Creating…' : 'Create meeting'}
      </Button>
      <span className="text-xs text-muted-foreground/70">You don't have to be travelling to it</span>
      {error && <span className="text-xs text-destructive">{error}</span>}
    </form>
  );
}

function EventCard({ e }: { e: TravelGroupSummary }) {
  return (
    <Link
      to={`/events/${e.id}`}
      className="flex flex-col gap-1 rounded-lg border border-border bg-background p-3 transition-colors hover:bg-muted/40"
    >
      <span className="text-sm font-medium text-foreground">{e.name}</span>
      <span className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          <MapPin className="h-3 w-3" aria-hidden="true" />
          {e.destination_airport ?? 'no city set'}
        </span>
        <span className="inline-flex items-center gap-1">
          <CalendarDays className="h-3 w-3" aria-hidden="true" />
          {anchorHeadline({
            start_at: e.anchor_start_at ?? null,
            timezone: e.anchor_timezone ?? null,
            label: null,
          })}
        </span>
        <span className="inline-flex items-center gap-1">
          <Users className="h-3 w-3" aria-hidden="true" />
          {eventSubtitle(e.attendees)}
        </span>
      </span>
    </Link>
  );
}

export default function EventsPage() {
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);

  const { data: events, isLoading } = useQuery({
    queryKey: ['organized-events'],
    queryFn: () => travelGroupApi.listEvents(),
  });

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-4 p-4">
      <header className="flex flex-wrap items-baseline justify-between gap-2">
        <h1 className="text-lg font-semibold">Meetings you organize</h1>
        {!creating && (
          <button
            onClick={() => setCreating(true)}
            className="inline-flex items-center gap-1 rounded-full border border-dashed border-border px-3 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted"
          >
            <Plus className="h-3 w-3" /> New meeting
          </button>
        )}
      </header>

      {creating && (
        <CreateEventForm
          onCreated={() => {
            setCreating(false);
            qc.invalidateQueries({ queryKey: ['organized-events'] });
          }}
        />
      )}

      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}

      {!isLoading && (events ?? []).length === 0 && !creating && (
        <p className="text-sm text-muted-foreground">
          No meetings yet. Create one to bring people into a city and out again — you can add
          attendees who have never used TripMuscle, and share an invite code with the ones who have.
        </p>
      )}

      <div className="flex flex-col gap-2">
        {(events ?? []).map((e) => (
          <EventCard key={e.id} e={e} />
        ))}
      </div>
    </div>
  );
}
